import { Box, Card, Container, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { memo, useCallback, useEffect } from "react";
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { DeviceWithLastAssetNotification, EldLandmark } from "src/models";
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";
import { formatDatetimeForPreview } from "src/utils/date-utils";
import { useAuthContext } from "src/auth/hooks";

import PageHeader from "src/components/page-header";
import NoneFound from "src/components/none-found";
import { useSort } from "src/hooks/use-sort";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useParams } from "src/routes/hooks";
import { useSearch } from "src/hooks/use-search";
import { SearchCard } from "src/components/search-card";
import dayjs from "dayjs";
import Logo from "src/components/logo";

type EldDevicesProps = {
  devices?: DeviceWithLastAssetNotification[]
}

const EldDevices = memo(({ devices }: EldDevicesProps) => {
  const { sortedList, sortData } = useSort(devices);

  const {
    searchText,
    setSearchText,
    filteredList,
  } = useSearch(sortedList, ["deviceType"]);

  const { session } = useAuthContext();
  const timezone = session?.organization?.companyTimezone as string;

  if (!sortedList) {
    return null;
  }

  return <div>
    <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor="Devices" />
    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TruxTableCell>
                Device Type
              </TruxTableCell>
              <TruxTableCell>
                Asset
              </TruxTableCell>
              <TruxTableCell>
                Entered
              </TruxTableCell>
              <TruxTableCell>
                Days Dormant
              </TruxTableCell>
              <TruxTableCell align="right">
                Location Last Reported
              </TruxTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredList.map((device, index) => {
              let deviceType = "";
              if (device.deviceType.startsWith("ELD Truck")) {
                deviceType = "Truck";
              } else if ((device.externalIdentifier || "").startsWith("1|")) {
                deviceType = "CT Trailer";
              } else if ((device.externalIdentifier || "").startsWith("2|")) {
                deviceType = "Samsara";
              } else if ((device.externalIdentifier || "").startsWith("3|")) {
                deviceType = "SkyBitz";
              } else if ((device.externalIdentifier || "").startsWith("4|")) {
                deviceType = "Premier";
              } else if ((device.externalIdentifier || "").startsWith("5|")) {
                deviceType = "CT Imobilizer";
              }
              return <TableRow key={index}>
                <TruxTableCell>
                  {deviceType}
                </TruxTableCell>
                <TruxTableCell>
                  {device.deviceType}
                </TruxTableCell>
                <TruxTableCell>
                  {device.assetNotification && formatDatetimeForPreview(device.assetNotification.notificationTime, timezone)}
                </TruxTableCell>
                <TruxTableCell>
                  {device.assetNotification && <>{dayjs().diff(dayjs(device.assetNotification.notificationTime), 'day')} Days</>}
                </TruxTableCell>
                <TruxTableCell align="right">
                  {formatDatetimeForPreview(device.lastPing, timezone)}
                </TruxTableCell>
              </TableRow>;
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

type PageModel = {
  pageData?: {
    devices?: DeviceWithLastAssetNotification[],
    eldLandmark?: EldLandmark,
    message?: string,
  }
}

export default function Page() {
  const settings = useSettingsContext();
  const { shareLinkUuid } = useParams();

  const dataLoader = useCallback(() => ({
    pageData: axios.get(
      formatEndpoint(endpoints.devices.findAllByShareLinkUuid, {shareLinkUuid})
    ).then(response => response.data)
  }), [shareLinkUuid]);

  const {
    data,
    updateData,
    setData,
    error,
    loading,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      alert(error);
    }
  }, [loading, error]);


  return (
    <Box>
      <Box sx={{background: "black", display: "flex", justifyContent: "start", pl: 2}}>
        <Logo sx={{display: "flex", alignItems: "center"}} />
      </Box>
      <Container maxWidth={settings.themeStretch ? false : 'lg'} sx={{p: 2}}>
        <DisplayPageData loading={loading} error={error} data={data}>
          {data && <>
            {data.pageData?.message ? <Box p={4}>
              <NoneFound title={data.pageData.message} message="Please contact the owner of the Account">
                <Logo dark />
              </NoneFound>
            </Box> : <>
              <PageHeader title={`Assets currently in '${data.pageData?.eldLandmark?.name}'`} />

              {data?.pageData?.devices?.length === 0 &&
                <NoneFound title={`No Devices Found in ${data.pageData?.eldLandmark?.name}`} message={`When devices enter ${data.pageData?.eldLandmark?.name} they will appear here.`} />
              }

              {data?.pageData?.devices?.length !== 0 && <EldDevices devices={data?.pageData?.devices} />}
            </>}
          </>}
        </DisplayPageData>
      </Container>
    </Box>
  );
}
